import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import type { Wallet } from "@/interfaces/wallet";
import type { WalletAsset } from "@/interfaces/asset";

import { makeRequest } from "@/utils/makeRequest";
import { queryClient } from "@/services/queryClient";
import MintContainer from "@/components/admin-components/mint/MintContainer";
import Loader from "@/components/loader/Loader";
import { ErrorState } from "@/components/ErrorState";

const AdminMint = () => {
  const { systemType } = useParams();

  const [selectedAsset, setSelectedAsset] = useState<WalletAsset>();

  const [isLoading, setIsLoading] = useState(false);

  const fetchWallet = async () => {
    const res = await makeRequest.get(
      `/wallet/system/${systemType!.toUpperCase()}`,
    );

    return res.data;
  };

  const {
    data: wallet,
    isLoading: isLoadingWallet,
    isError,
    refetch,
  } = useQuery<Wallet>({
    queryKey: ["wallet:details", systemType],
    enabled: !!systemType,
    staleTime: 1000 * 60,
    retry: 2,
    queryFn: fetchWallet,
  });

  const assets = wallet?.assets ?? [];

  useEffect(() => {
    if (!assets.length) return;

    if (selectedAsset) return;

    setSelectedAsset(assets[0]);
  }, [assets, selectedAsset]);

  const mint = async ({
    assetId,
    networkId,
    amount,
    otp,
    securityCode,
  }: {
    assetId: string;
    networkId: string;
    amount: string;
    otp: string;
    securityCode: string;
  }) => {
    setIsLoading(true);

    try {
      await makeRequest.post("/blockchain/mint", {
        assetId,
        networkId,
        amount,
        otp,
        securityCode,
      });

      await queryClient.invalidateQueries({
        queryKey: ["wallet:details", systemType],
      });

      await queryClient.refetchQueries({
        queryKey: ["wallet:details", systemType],
      });

      // await refetch();
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingWallet) {
    return <Loader type="bounce" />;
  }

  if (!wallet) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        Wallet not found.
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <MintContainer
      wallet={wallet}
      assets={assets}
      initialAsset={selectedAsset}
      isLoading={isLoading}
      onMint={mint}
      onSelectedAssetChange={setSelectedAsset}
    />
  );
};

export default AdminMint;
